import { Plus } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { PageHeader } from '@/components/common/PageHeader'
import { ErrorState } from '@/components/common/States'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuth } from '@/lib/auth/AuthProvider'
import { useProject } from '../api'
import { PROJECT_WRITE_PERM } from '../permissions'
import type { Project } from '../types'
import { useSelectedProjectId } from '../ui'
import { ProjectCards } from './ProjectCards'
import { ProjectFormModal } from './ProjectFormModal'
import { ProjectHeader } from './ProjectHeader'

type Props = {
  title: string
  description: string
  createLabel: string
  children: (project: Project) => ReactNode
}

/** Proje sayfalarının ortak iskeleti: başlık, proje kartları ve seçili projenin detay kartı. */
export function ProjectWorkspace({ title, description, createLabel, children }: Props) {
  const { t } = useTranslation()
  const { can } = useAuth()
  const { projects, selectedId, select } = useSelectedProjectId()
  const project = useProject(selectedId)
  const [dialog, setDialog] = useState<{ open: boolean; edit: boolean }>({ open: false, edit: false })
  const canWrite = can(PROJECT_WRITE_PERM)

  const createButton = canWrite ? (
    <Button onClick={() => setDialog({ open: true, edit: false })}>
      <Plus />
      {createLabel}
    </Button>
  ) : null

  return (
    <>
      <PageHeader eyebrow={t('nav.groups.projects')} title={title} description={description} actions={createButton} />

      {projects.isError ? (
        <div className="rounded-xl border bg-card shadow-card">
          <ErrorState error={projects.error} onRetry={() => void projects.refetch()} />
        </div>
      ) : (
        <div className="space-y-6">
          <ProjectCards
            projects={projects.data ?? []}
            loading={projects.isLoading}
            selectedId={selectedId}
            onSelect={select}
            emptyAction={createButton}
          />

          {selectedId ? (
            <section className="overflow-hidden rounded-xl border bg-card shadow-card" aria-live="polite">
              {project.isError ? (
                <ErrorState error={project.error} onRetry={() => void project.refetch()} />
              ) : project.data ? (
                <>
                  <ProjectHeader project={project.data} onEdit={() => setDialog({ open: true, edit: true })} onDeleted={() => select(null)} />
                  {children(project.data)}
                </>
              ) : (
                <div className="space-y-3 p-5">
                  <Skeleton className="h-14 w-2/3" />
                  <Skeleton className="h-64" />
                </div>
              )}
            </section>
          ) : null}
        </div>
      )}

      <ProjectFormModal
        open={dialog.open}
        project={dialog.edit ? (project.data ?? null) : null}
        onOpenChange={(open) => setDialog((s) => ({ ...s, open }))}
        onCreated={select}
      />
    </>
  )
}
